<template>
  <crud-modal
    :title="$t('components.materialTemplate.createModal.title')"
    action="create"
    :on-submit="submit"
    :on-close="() => $emit('close')"
    :error-message="errorMessage"
  >
    <div class="mb-6">
      <input-text-validated
        ref="displayName"
        v-model="generalInputs.displayName"
        input-class="w-full"
        :label="$t('components.materialTemplate.createModal.displayName')"
        rules="required"
        placeholder="Social Media Bild 1"
      />
    </div>

    <div class="mb-6">
      <input-text-validated
        ref="prettyFileName"
        v-model="generalInputs.prettyFileName"
        input-class="w-full"
        :label="$t('components.materialTemplate.createModal.prettyFileName')"
        rules="required"
        placeholder="GWsocialbild-situations-analyse"
      />
    </div>

    <div
      v-if="landingpageTemplateId"
      class="mb-6"
    >
      <input-select-validated
        ref="type"
        v-model="generalInputs.type"
        :label="$t('components.materialTemplate.createModal.type')"
        :options="availableTypesForLandingpages"
        rules="required"
      />
    </div>

    <div
      v-if="landingpageTemplateId"
      class="mb-6"
    >
      <input
        ref="addToExistingLandingpages"
        v-model="generalInputs.addToExistingLandingpages"
        type="checkbox"
      >
      {{ $t('components.materialTemplate.createModal.addToExistingLandingpages') }}
    </div>

    <div class="mb-6">
      <input-select-validated
        ref="assetType"
        v-model="assetType"
        :label="$t('components.materialTemplate.createModal.assetType')"
        :options="ASSET_TYPES"
        rules="required"
      />
    </div>

    <div class="mb-6">
      <input-select-validated
        ref="generationService"
        v-model="selectedGenerationService"
        :label="$t('components.materialTemplate.createModal.service')"
        :options="availableGenerationServices"
        rules="required"
      />
    </div>

    <BrandEasy
      v-if="selectedGenerationService === 'BRAND_EASY'"
      v-model="brandEasyInputs"
    />

    <Creatify
      v-if="selectedGenerationService === 'CREATIFY'"
      v-model="creatifyConfig"
    />

    <Static
      v-if="selectedGenerationService === 'STATIC'"
      v-model="staticInputs"
    />
  </crud-modal>
</template>

<script>
import CrudModal from '@/components/modals/crud-modal'
import InputSelectValidated from '@/components/form/select-validated'
import InputTextValidated from '@/components/form/text-validated'
import BrandEasy from './brand-easy.vue'
import Creatify from './creatify.vue'
import Static from './static.vue'
import CREATE_BRAND_EASY_MATERIAL_TEMPLATE from '@/graphql/CreateBrandEasyMaterialTemplate.gql'
import CREATE_VIDEO_MATERIAL_TEMPLATE from '@/graphql/CreateVideoMaterialTemplate.gql'
import GET_LANDINGPAGE_TEMPLATE from '@/graphql/GetLandingpageTemplate.gql'

export default {
  components: {
    CrudModal,
    InputSelectValidated,
    InputTextValidated,
    BrandEasy,
    Creatify,
    Static
  },

  props: {
    landingpageTemplateId: {
      type: String,
      default: null
    },
    campaignId: {
      type: String,
      default: null
    }
  },

  data () {
    return {
      errorMessage: '',
      selectedGenerationService: 'BRAND_EASY',

      generalInputs: {
        type: this.campaignId ? 'CAMPAIGN' : 'DOWNLOAD',
        displayName: '',
        prettyFileName: '',
        addToExistingLandingpages: true
      },

      brandEasyInputs: {
        target: '',
        template: this.campaignId ? 'bot_kam_{variant}-' : 'bot_rat_{variant}-',
        bleed: '0,0,0,0'
      },

      creatifyConfig: {
        templateId: '',
        variants: []
      },

      staticInputs: {
        // To be implemented
      },

      assetType: '',

      ASSET_TYPES: [
        { value: 'SHOPPING_CART', name: 'SHOPPING_CART' },
        { value: 'CHECKLIST', name: 'CHECKLIST' },
        { value: 'UNKNOWN', name: 'UNKNOWN' },
        { value: 'AD', name: 'AD' },
        { value: 'FLYER_A5', name: 'FLYER_A5' },
        { value: 'FLYER', name: 'FLYER' },
        { value: 'POSTER', name: 'POSTER' },
        { value: 'BANNER', name: 'BANNER' },
        { value: 'SOCIAL_IMAGE', name: 'SOCIAL_IMAGE' },
        { value: 'SOCIAL_STORY', name: 'SOCIAL_STORY' },
        { value: 'COASTER', name: 'COASTER' },
        { value: 'POSTCARD_BLANK', name: 'POSTCARD_BLANK' },
        { value: 'POSTCARD_TEXT', name: 'POSTCARD_TEXT' },
        { value: 'NEWSPAPER', name: 'NEWSPAPER' },
        { value: 'GUIDE', name: 'GUIDE' },
        { value: 'VIDEO', name: 'VIDEO' }
      ]
    }
  },

  computed: {
    availableTypesForLandingpages () {
      // eslint-disable-next-line no-void
      void this.$i18n.locale
      return [
        { value: 'DOWNLOAD', name: this.$t('components.materialTemplate.createModal.typeDownload') },
        { value: 'BASIC_AD', name: this.$t('components.materialTemplate.createModal.typeBasicAd') }
      ]
    },
    availableGenerationServices () {
      // eslint-disable-next-line no-void
      void this.$i18n.locale
      return [
        { value: 'BRAND_EASY', name: 'BrandEasy' },
        { value: 'CREATIFY', name: 'Creatify' },
        { value: 'STATIC', name: this.$t('components.materialTemplate.createModal.generationServiceStatic') }
      ]
    }
  },

  methods: {
    async submit () {
      this.errorMessage = ''

      return ({
        BRAND_EASY: this.submitBrandEasy,
        CREATIFY: this.submitCreatify,
        STATIC: this.submitStatic
      })[this.selectedGenerationService]()
    },

    async submitBrandEasy () {
      try {
        await this.$apollo.mutate({
          mutation: CREATE_BRAND_EASY_MATERIAL_TEMPLATE,
          variables: {
            input: {
              ...this.generalInputs,
              brandEasyConfig: this.brandEasyInputs,
              landingpageTemplateId: this.landingpageTemplateId,
              campaignId: this.campaignId,
              assetType: this.assetType
            }
          },
          // Currently update / removing MaterialTemplate from cache only works for LandingpageTemplates.
          // A better way needs to be found when implementing it for campaigns.
          // Nicest way would be to use events (this.$emit('added')) but don't know how to access it from
          // outside because it's an (CRUD)-modal.
          update: (cache, { data: { materialTemplate } }) => {
            const params = {
              query: GET_LANDINGPAGE_TEMPLATE,
              variables: {
                input: {
                  landingpageTemplateId: this.landingpageTemplateId
                }
              }
            }
            const data = cache.readQuery(params)
            data.landingpageTemplate.materialTemplates.push(materialTemplate)
            cache.writeQuery({
              ...params,
              data
            })
          }
        })
      } catch (err) {
        this.errorMessage = err.graphQLErrors?.[0]?.message || err.message
        throw err
      }
    },

    async submitCreatify () {
      try {
        await this.$apollo.mutate({
          mutation: CREATE_VIDEO_MATERIAL_TEMPLATE,
          variables: {
            input: {
              ...this.generalInputs,
              creatifyConfig: this.creatifyConfig,
              landingpageTemplateId: this.landingpageTemplateId,
              assetType: this.assetType
            }
          },
          refetchQueries: [{
            query: GET_LANDINGPAGE_TEMPLATE,
            variables: {
              input: { landingpageTemplateId: this.landingpageTemplateId }
            }
          }]
        })
      } catch (err) {
        this.errorMessage = err.graphQLErrors?.[0]?.message || err.message
        throw err
      }
    },

    async submitStatic () {
      throw new Error('To be implemented')
    }
  }
}
</script>
